import { useLayoutEffect, useRef, useState } from 'react';
import { Button, FormField } from '../../../school_panel/components/ui';
import { fieldClass } from '../../../school_panel/lib/fieldClass';
import { blankCount, insertBlank, renumber, snapCursor } from '../fillBlank';
import { ChipsInput, FieldError, FormFooter, type FormProps, SourceFields, useQuestionSource } from './shared';

const MAX_BLANKS = 10;
const MARKER_ID = /\[\[(\d+)\]\]/g;

function Preview({ text }: { text: string }) {
  const parts = text.split(/(\[\[\d+\]\])/g);
  return (
    <p className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-ink" aria-label="Preview">
      {parts.map((p, i) => {
        const m = /^\[\[(\d+)\]\]$/.exec(p);
        return m
          ? <span key={i} className="mx-0.5 inline-block min-w-[1.5rem] rounded border border-primary px-1 text-center text-xs text-primary">{m[1]}</span>
          : <span key={i}>{p}</span>;
      })}
    </p>
  );
}

export default function FillBlankForm({ initial, saving, errors, onSave, onCancel, chapters }: FormProps) {
  // Questions from other clients may have gaps or out-of-order markers: renumber once
  // on load and keep each blank's answers with its marker.
  const [start] = useState(() => {
    const t = initial?.text ?? '';
    const given = initial?.blanks ?? [];
    const ids = Array.from(t.matchAll(MARKER_ID), (m) => Number(m[1]));
    return { text: renumber(t), blanks: ids.length ? ids.map((id) => [...(given[id - 1] ?? [])]) : given.map((b) => [...b]) };
  });
  const [text, setText] = useState(start.text);
  const [blanks, setBlanks] = useState<string[][]>(start.blanks);
  const [explanation, setExplanation] = useState(initial?.explanation ?? '');
  const source = useQuestionSource(initial);
  const [local, setLocal] = useState<Record<string, string>>({});
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingCursor = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (pendingCursor.current != null && ref.current) {
      ref.current.setSelectionRange(pendingCursor.current, pendingCursor.current);
      ref.current.focus();
      pendingCursor.current = null;
    }
  });

  const onTextChange = (value: string) => {
    // Markers are always numbered 1..n in the stored text, so a marker's number
    // is the index of the blank it carried before this edit.
    const ids = Array.from(value.matchAll(MARKER_ID), (m) => Number(m[1]));
    setBlanks(ids.map((id) => blanks[id - 1] ?? []));
    const fixed = renumber(value);
    if (fixed !== value) pendingCursor.current = Math.min(ref.current?.selectionStart ?? fixed.length, fixed.length);
    setText(fixed);
  };

  const onInsert = () => {
    if (blankCount(text) >= MAX_BLANKS) return;
    const cursor = snapCursor(text, ref.current?.selectionStart ?? text.length);
    const before = blankCount(text.slice(0, cursor));
    const r = insertBlank(text, cursor);
    setBlanks([...blanks.slice(0, before), [], ...blanks.slice(before)]);
    setText(r.text);
    pendingCursor.current = r.cursor;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    const n = blankCount(text);
    if (!text.trim()) err.text = 'Enter the question.';
    else if (n === 0) err.blanks = 'Add at least one blank.';
    else if (n > MAX_BLANKS) err.blanks = `At most ${MAX_BLANKS} blanks.`;
    else if (blanks.slice(0, n).some((b) => b.length === 0)) err.blanks = 'Add an accepted answer for every blank.';
    setLocal(err);
    if (Object.keys(err).length) return;
    onSave({
      type: 'fill_blank', text, blanks: blanks.slice(0, n),
      ...(explanation.trim() ? { explanation: explanation.trim() } : {}), ...source.payload,
    });
  };
  const e = { ...local, ...errors };
  const n = blankCount(text);

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <FormField label="Question text" htmlFor="fb-text" required error={e.text}
        hint="Write the sentence, then put the cursor where a word is missing and press Insert blank.">
        <textarea id="fb-text" ref={ref} rows={3} className={fieldClass} value={text} onChange={(ev) => onTextChange(ev.target.value)} />
      </FormField>
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={onInsert} disabled={n >= MAX_BLANKS}>Insert blank</Button>
        <span className="text-xs text-muted">{n >= MAX_BLANKS ? `At most ${MAX_BLANKS} blanks` : `${n} of ${MAX_BLANKS} blanks`}</span>
      </div>
      {text && <Preview text={text} />}
      {n > 0 && (
        <div className="space-y-2">
          {Array.from({ length: n }, (_, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="mt-2 inline-block min-w-[1.5rem] rounded border border-primary px-1 text-center text-xs text-primary">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <ChipsInput label={`Accepted answers for blank ${i + 1}`} values={blanks[i] ?? []}
                  onChange={(v) => setBlanks(Array.from({ length: n }, (_, j) => (j === i ? v : blanks[j] ?? [])))} />
              </div>
            </div>
          ))}
        </div>
      )}
      <FieldError error={e.blanks} />
      <FormField label="Explanation (optional)" htmlFor="fb-expl" error={e.explanation}>
        <textarea id="fb-expl" rows={2} className={fieldClass} value={explanation} onChange={(ev) => setExplanation(ev.target.value)} />
      </FormField>
      <SourceFields source={source} chapters={chapters} errors={e} idPrefix="fb" />
      <FormFooter saving={saving} onCancel={onCancel} />
    </form>
  );
}
